import firebase from "firebase/app";

import { Button } from "semantic-ui-react";

import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { db } from "astrid-firebase";
import getArticlesQuery from "astrid-firestore/src/api/articles/getArticlesQuery";
import createProductionNew from "astrid-firestore/src/api/productions/createProductionNew";
import createWorkDraftData from "astrid-firestore/src/api/works/createWorkDraftData";
import { useCollectionDataOnce } from "astrid-firestore/src/hooks/index";
import useCollectionData from "astrid-firestore/src/hooks/useCollectionData";
import useDocumentData from "astrid-firestore/src/hooks/useDocumentData";
import getCollectionDataById from "astrid-firestore/src/utils/getCollectionDataById";
import getDocumentData from "astrid-firestore/src/utils/getDocumentData";

import Flex from "../../../../components/Flex/Flex";
import InstantSearchSelect from "../../../ui/components/InstantSearch/InstantSearchSelect";
import OpenButton from "../../../ui/components/OpenButton/OpenButton";

import AddArticleModal from "../../../articles/components/AddArticleModal/AddArticleModal";
import { useProfile } from "../../../authentication/state/profile";
import Form from "../../../forms/components/Form/Form";
import useSimpleForm from "../../../forms/hooks/useSimpleForm";
import ReaderSearchForm from "../../../readers/components/ReaderSearchForm/ReaderSearchForm";
import usePublisherManagerOptions from "../../../users/hooks/usePublisherManagerOptions";
import WorkMetaDataEdit from "../../../works/components/WorkMetaDataEdit/WorkMetaDataEdit";

import useProductionTypeOptions from "../../hooks/useProductionTypeOptions";

import CreateProductionArticleListItem from "./components/CreateProductionArticleListItem";
import CreateProductionPublisherListItem from "./components/CreateProductionPublisherListItem";
import CreateProductionReaderListItem from "./components/CreateProductionReaderListItem";
import CreateProductionSegment from "./components/CreateProductionSegment";
import CreateProductionWorkListItem from "./components/CreateProductionWorkListItem";
import CreateProductionWorkOption from "./components/CreateProductionWorkOption";

export default function CreateProductionProducerFormNew({ publishers, work: defaultWork, article: defaultArticle }) {
	const { t } = useTranslation(["common", "language"]);
	const user = useProfile();
	const navigate = useNavigate();

	const [channels] = useCollectionDataOnce(db.collection("distributionChannels").where("deleted", "==", false));

	const publisherOptions = (publishers || []).map((publisher) => ({
		key: publisher.id,
		value: publisher,
		text: publisher.name,
	}));

	const defaultPublisher = publishers?.find((publisher) => publisher.id === defaultWork?.publisher?.id) || null;

	const [currentIndex, setCurrentIndex] = useState(defaultArticle ? 2 : 1);

	const [workDraft, setWorkDraft] = useState(null);

	const { form, watch, setValue, onSubmit, formState } = useSimpleForm({
		values: {
			publisher: defaultPublisher,
			workId: defaultWork?.id || null,
			articleIds: defaultArticle ? [defaultArticle.id] : [],
			readers: [],
			publisherManagers: [],
			deliveryDate: null,
			productionDate: null,
			type: null,
		},
		onSubmit: async (
			{ publisher, workId, articleIds, readers, deliveryDate, productionDate, type, publisherManagers },
			{ draft },
		) => {
			const work = await getDocumentData(db.collection("works").doc(workId));
			const articles = await getCollectionDataById(firebase, db.collection("articles"), articleIds);

			return createProductionNew(
				firebase,
				{
					draft,
					work,
					publisher,
					deliveryDate,
					productionDate,
					productionType: type,
					publisherManagers,
					createdBy: user,
				},
				{
					articles,
					readers,
					discussion: !!publisher.featureFlags?.includes("discussion"),
				},
			);
		},
		onSuccess: ({ id }) => {
			navigate(`/production/${id}`);
		},
	});

	const { publisher, workId, articleIds, readers, deliveryDate, publisherManagers } = watch();

	const [work] = useDocumentData(workId && db.collection("works").doc(workId));
	const [workArticles] = useCollectionData(work && getArticlesQuery(firebase, { work }));

	const articles = useMemo(
		() => workArticles?.filter(({ id }) => articleIds.includes(id)) || [],
		[articleIds, workArticles],
	);

	const [publisherManagerOptions, publisherManagersLoading] = usePublisherManagerOptions(publisher?.id);
	const typeOptions = useProductionTypeOptions();

	const skipArticlesStep = workArticles?.length === 0;

	useEffect(() => {
		if (!publisher) {
			setCurrentIndex(1);
		} else if (!work) {
			setCurrentIndex(2);
		} else if (articles?.length <= 0) {
			setCurrentIndex(3);
		}
	}, [publisher, work, articles]);

	useEffect(() => {
		if (currentIndex === 3 && skipArticlesStep) {
			setCurrentIndex(4);
		}
	}, [currentIndex, skipArticlesStep]);

	return (
		<>
			<Form form={form}>
				<CreateProductionSegment
					index={1}
					currentIndex={currentIndex}
					setCurrentIndex={setCurrentIndex}
					header={t("selectPublisher", "Select publisher")}
					nextButtonDisabled={!publisher}
				>
					{currentIndex === 1 && !publisher && (
						<Form.Select
							name="publisher"
							label={t("publisher", "Publisher")}
							search
							deburr
							options={publisherOptions}
						/>
					)}

					{publisher && (
						<Flex flexDirection="column" gap={10}>
							<CreateProductionPublisherListItem
								publisher={publisher}
								onClick={() => {
									setValue("publisher", null);
									setValue("workId", null);
									setValue("articleIds", []);
									setValue("readers", []);
									setValue("publisherManagers", []);
								}}
							/>
						</Flex>
					)}
				</CreateProductionSegment>

				<CreateProductionSegment
					index={2}
					currentIndex={currentIndex}
					setCurrentIndex={setCurrentIndex}
					header={t("selectWork", "Select work")}
					subHeader={t("createNewOrExisting", "Create new or select an existing one")}
					nextButtonDisabled={!work?.language}
				>
					{!work?.exists ? (
						<InstantSearchSelect
							indexName="works"
							allowAdditions
							filters={`publisher.id:${publisher?.id}`}
							getOption={(work) => {
								return {
									key: work.id,
									value: work.id,
									text: work.name,
									content: <CreateProductionWorkOption work={work} />,
									data: work,
								};
							}}
							onChange={(e, { value, option }) => {
								if (option) {
									setValue("workId", value);
								} else if (value) {
									setWorkDraft(createWorkDraftData({ name: value, publisher }));
								}
							}}
						/>
					) : (
						<CreateProductionWorkListItem
							work={work}
							onClick={() => {
								setValue("articleIds", []);
								setValue("workId", null);
								setValue("readers", []);
							}}
						/>
					)}
				</CreateProductionSegment>

				{!skipArticlesStep && (
					<CreateProductionSegment
						index={3}
						currentIndex={currentIndex}
						setCurrentIndex={setCurrentIndex}
						header={t("selectArticles", "Select articles")}
						subHeader={t("createNewOrExisting", "Create new or select an existing one")}
						showSkip={!articles.length > 0}
					>
						<Flex flexDirection="column" alignItems="center" gap={20}>
							{articles.length > 0 && (
								<Flex flexDirection="column" gap={10}>
									{articles.map((article) => {
										return (
											<CreateProductionArticleListItem
												key={article.id}
												article={article}
												onClick={() =>
													setValue(
														"articleIds",
														articleIds.filter((id) => id !== article.id),
													)
												}
											/>
										);
									})}
								</Flex>
							)}

							{currentIndex >= 3 && (
								<OpenButton primary basic circular icon="plus" style={{ margin: 0 }}>
									{({ setOpen }) => (
										<AddArticleModal
											work={work}
											channels={channels}
											publisher={publisher}
											articleIds={articles.map((a) => a.id)}
											disableAdd
											onSubmit={({ articleIds }) => {
												setValue("articleIds", articleIds);
											}}
											onClose={() => setOpen(false)}
										/>
									)}
								</OpenButton>
							)}
						</Flex>
					</CreateProductionSegment>
				)}

				<CreateProductionSegment
					index={4}
					currentIndex={currentIndex}
					setCurrentIndex={setCurrentIndex}
					header={t("selectNarrators", "Select narrators")}
					subHeader={t("narratorsWillGetOffer", "The narrators you select will receive an offer")}
					showSkip={readers?.length <= 0}
				>
					<Flex flexDirection="column" alignItems="center" gap={20}>
						{readers.length > 0 && (
							<Flex flexDirection="column" gap={10}>
								{readers.map((reader) => {
									return (
										<CreateProductionReaderListItem
											key={reader.id}
											reader={reader}
											onClick={() =>
												setValue(
													"readers",
													readers.filter((r) => r.id !== reader.id),
												)
											}
										/>
									);
								})}
							</Flex>
						)}

						{currentIndex >= 4 && (
							<OpenButton primary basic circular icon="plus" style={{ margin: 0 }}>
								{({ setOpen }) => (
									<ReaderSearchForm
										language={work.language}
										onSubmit={({ reader }) => {
											setValue("readers", [...readers, reader]);
											setOpen(false);
										}}
										submitText={t("add", "Add")}
										onClose={() => setOpen(false)}
									/>
								)}
							</OpenButton>
						)}
					</Flex>
				</CreateProductionSegment>

				<CreateProductionSegment
					index={5}
					currentIndex={currentIndex}
					setCurrentIndex={setCurrentIndex}
					header={t("otherInformation", "Other information")}
					subHeader={t("createProductionLastInfo", "Add this last bit of information and you're done!")}
					lastCheckMark={deliveryDate && publisherManagers.length > 0}
					finalStep
				>
					{currentIndex === 5 && (
						<>
							<Form.Group widths="equal" style={{ maxWidth: 500, margin: "auto" }}>
								<Form.DateInput
									name="deliveryDate"
									label={t("deliveryDate", "Delivery date")}
									minDate={new Date()}
								/>

								<Form.DocumentSelect
									name="publisherManagers"
									label={t("publisherManager", "Publisher manager")}
									multiple
									search
									deburr
									options={publisherManagerOptions}
									loading={publisherManagersLoading}
								/>
							</Form.Group>

							<Form.Group widths="equal" style={{ maxWidth: 500, margin: "auto" }}>
								<Form.DateInput
									name="productionDate"
									label={t("productionDueDate", "Due date")}
									minDate={new Date()}
								/>

								<Form.Select
									name="type"
									label={t("type", "Type")}
									placeholder={t("internal", "Internal")}
									deburr
									options={typeOptions}
									rules={{ required: false }}
								/>
							</Form.Group>
						</>
					)}
				</CreateProductionSegment>

				<Flex justifyContent="center" gap={10} style={{ marginTop: 15 }}>
					<Button
						loading={formState.isSubmitting}
						disabled={formState.isSubmitting || !formState.isValid || !work || currentIndex !== 5}
						content={t("createDraft", "Create draft")}
						onClick={() => onSubmit({ draft: true })}
					/>

					<Button
						loading={formState.isSubmitting}
						disabled={formState.isSubmitting || !formState.isValid || !work || currentIndex !== 5}
						primary
						content={t("createOffer", "Create offer")}
						onClick={() => onSubmit({ draft: false })}
					/>
				</Flex>
			</Form>

			{workDraft && (
				<WorkMetaDataEdit
					work={workDraft}
					publisher={publisher}
					onSuccess={(newWork) => {
						setValue("workId", newWork.id);
						setWorkDraft(null);
					}}
					onClose={() => {
						setWorkDraft(null);
					}}
				/>
			)}
		</>
	);
}
